import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Linkedin, Share2, Check, Link as LinkIcon } from 'lucide-react';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/stories/${slug}`);
        setProfile(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [slug]);

  const PRODUCTION_DOMAIN = process.env.REACT_APP_PUBLIC_DOMAIN || (typeof window !== 'undefined' ? window.location.origin : 'https://shebuiltthis.co');
  const getShareUrl = () => `${PRODUCTION_DOMAIN}/api/share/profile/${slug}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { setCopied(false); }
  };

  const shareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl())}`, '_blank');
  };
  const shareTwitter = () => {
    const text = encodeURIComponent(`${profile?.name} — ${profile?.title}. Read her verified profile on @SheBuiltThis`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(getShareUrl())}`, '_blank');
  };
  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`Check out ${profile?.name}'s profile: ${getShareUrl()}`)}`, '_blank');
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '';
  const getRegionColor = (region) => {
    const c = { 'Middle East & North Africa': '#C25E3E', 'South Asia': '#2E8B57', 'East Asia': '#4A6FA5', 'Southeast Asia & Pacific': '#1A8A7D', 'Sub-Saharan Africa': '#C4792E', 'North America': '#5A7FB5', 'Latin America & Caribbean': '#A8416D', 'Europe Western': '#7A5BA5', 'Europe Eastern & Nordic': '#3A8A8A', 'Central Asia & Caucasus': '#A8872E', 'Oceania & Australia': '#2A93A8' };
    return c[region] || '#C25E3E';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] pt-40">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="animate-pulse grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2"><div className="aspect-[3/4] bg-[#F3EFE9]" /></div>
            <div className="lg:col-span-3 space-y-4"><div className="h-10 bg-[#F3EFE9] w-3/4" /><div className="h-6 bg-[#F3EFE9] w-1/2" /><div className="h-40 bg-[#F3EFE9] mt-8" /></div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] pt-40 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>Profile Not Found</h1>
          <Link to="/profiles" className="text-[#C25E3E] hover:underline" style={{ fontFamily: "'Outfit', sans-serif" }}>Back to Profiles</Link>
        </div>
      </div>
    );
  }

  const hasImage = profile.image_url && profile.image_url.trim() !== '';

  return (
    <div data-testid="profile-page" className="min-h-screen bg-[#FAF9F6] pt-32">
      <SEO
        title={`${profile.name} — ${profile.title}`}
        description={profile.hook || `${profile.name}, ${profile.title} at ${profile.company}.`}
        url={`https://shebuiltthis.com/profiles/${slug}`}
        image={profile.image_url}
        type="profile"
      />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-8">
        {/* Back */}
        <Link to="/profiles" className="inline-flex items-center gap-2 text-[13px] text-[#888] hover:text-[#C25E3E] transition-colors mb-10" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="back-to-profiles">
          <ArrowLeft size={16} /> Back to Profiles
        </Link>

        {/* Breadcrumb */}
        <p className="text-[11px] tracking-wide mb-8" style={{ fontFamily: "'Outfit', sans-serif", color: '#888' }}>
          {profile.region} &rsaquo; {profile.sector} &rsaquo; {profile.name}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* LEFT — Photo + Info */}
          <div className="lg:col-span-2">
            <div className="relative aspect-[3/4] mb-6 overflow-hidden bg-[#F3EFE9]">
              {hasImage ? (
                <img src={profile.image_url} alt={profile.name} className="w-full h-full object-cover" data-testid="profile-image" />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: getRegionColor(profile.region) }}>
                  <span className="text-8xl font-light text-white/80" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{getInitials(profile.name)}</span>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#C25E3E]" />
            </div>

            {profile.photo_credit && (
              <p className="text-[10px] mb-6" style={{ fontFamily: "'DM Mono', monospace", color: '#888' }}>Photo: {profile.photo_credit}</p>
            )}

            <h1 className="text-3xl lg:text-4xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: '#1A1A1A' }} data-testid="profile-name">
              {profile.name}
            </h1>
            <p className="text-lg mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#C25E3E' }}>{profile.title}</p>
            <p className="text-[13px] mb-1" style={{ fontFamily: "'Outfit', sans-serif", color: '#4A4A4A' }}>{profile.company}</p>
            <p className="text-[13px]" style={{ fontFamily: "'Outfit', sans-serif", color: '#888' }}>{profile.location}</p>

            <div className="flex flex-wrap gap-2 mt-4">
              <span className="px-3 py-1 text-[10px] tracking-[0.12em] uppercase border border-[#C25E3E] text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>{profile.region}</span>
              <span className="px-3 py-1 text-[10px] tracking-[0.12em] uppercase border border-[#2E8B57] text-[#2E8B57]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>{profile.sector}</span>
            </div>

            <div className="w-12 h-[2px] bg-[#C25E3E] my-6" />
          </div>

          {/* RIGHT — Story */}
          <div className="lg:col-span-3">
            {/* Verified */}
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#2E8B57] text-white text-[11px] tracking-wide" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                <Check size={14} /> Verified Profile
              </span>
            </div>

            {profile.is_founder && (
              <div className="mb-6">
                <span className="px-5 py-2 text-[11px] border-2 border-[#C25E3E] text-[#C25E3E] tracking-[0.15em] uppercase" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>FOUNDER · Profile #001</span>
              </div>
            )}

            {/* Hook */}
            {profile.hook && (
              <p className="text-xl lg:text-2xl italic mb-8 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: '#1A1A1A' }}>
                &ldquo;{profile.hook}&rdquo;
              </p>
            )}

            {/* Story */}
            <div className="text-[16px] leading-[1.9] whitespace-pre-line" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }} data-testid="profile-story">
              {profile.story}
            </div>

            {/* Key Facts */}
            {profile.key_facts && profile.key_facts.length > 0 && (
              <div className="mt-12 p-8 bg-[#F3EFE9] border-l-[3px] border-[#C25E3E]">
                <h3 className="text-[11px] tracking-[0.2em] uppercase mb-6" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>Key Facts</h3>
                <dl className="space-y-3">
                  {profile.key_facts.map((fact, i) => (
                    <div key={i} className="flex">
                      <dt className="text-[13px] w-36 flex-shrink-0" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#1A1A1A' }}>{fact.label}</dt>
                      <dd className="text-[14px]" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>{fact.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {/* Share */}
            <div className="mt-12 pt-8 border-t border-[#1A1A1A]/10">
              <h3 className="text-[11px] tracking-[0.2em] uppercase mb-6" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>Share This Profile</h3>
              <div className="flex flex-wrap gap-2">
                <button onClick={copyLink} data-testid="copy-link" className="flex items-center gap-2 px-4 py-2.5 border border-[#1A1A1A]/15 text-[#1A1A1A] text-[11px] tracking-[0.1em] uppercase hover:border-[#C25E3E] hover:text-[#C25E3E] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
                  {copied ? <Check size={13} /> : <LinkIcon size={13} />} {copied ? 'COPIED' : 'COPY LINK'}
                </button>
                <button onClick={shareLinkedIn} data-testid="share-linkedin" className="flex items-center gap-2 px-4 py-2.5 border border-[#1A1A1A]/15 text-[#1A1A1A] text-[11px] tracking-[0.1em] uppercase hover:border-[#0A66C2] hover:text-[#0A66C2] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
                  <Linkedin size={13} /> LINKEDIN
                </button>
                <button onClick={shareTwitter} data-testid="share-twitter" className="flex items-center gap-2 px-4 py-2.5 border border-[#1A1A1A]/15 text-[#1A1A1A] text-[11px] tracking-[0.1em] uppercase hover:border-[#1A1A1A] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
                  <Share2 size={13} /> X / TWITTER
                </button>
                <button onClick={shareWhatsApp} data-testid="share-whatsapp" className="flex items-center gap-2 px-4 py-2.5 border border-[#1A1A1A]/15 text-[#1A1A1A] text-[11px] tracking-[0.1em] uppercase hover:border-[#25D366] hover:text-[#25D366] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
                  WHATSAPP
                </button>
              </div>
            </div>

            {/* Sources */}
            {profile.sources && profile.sources.length > 0 && (
              <div className="mt-10 pt-6 border-t border-[#1A1A1A]/10">
                <h3 className="text-[11px] tracking-[0.2em] uppercase mb-4" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#888' }}>Sources</h3>
                <ul className="space-y-2">
                  {profile.sources.map((source, i) => (
                    <li key={i}>
                      <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-[13px] text-[#C25E3E] hover:underline" style={{ fontFamily: "'Outfit', sans-serif" }}>{source.name}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Nominate */}
            <div className="mt-10 pt-6 border-t border-[#1A1A1A]/10">
              <Link to="/nominate" className="text-[13px] text-[#C25E3E] hover:text-[#1A1A1A] transition-colors" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
                Nominate another woman like her &rarr;
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
